import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import _ from 'lodash';
import axios from 'axios';
import Title from '../../commonComponents/Title';
import translate from '../../commonComponents/translate';
import ContentContainer from '../../commonComponents/pageLayout/ContentContainer';
import SubHeader from '../../commonComponents/pageLayout/SubHeader';
import OrderProp from './propTypes/orderProp';
import {
  findCurrencySymbol,
  formatPrice,
  handleInitTab,
  PAYMENT_METHOD,
  returnAddress,
} from '../../services/utilityFunctions';
import Anchor from '../../commonComponents/Anchor';
import { hideLoader, showLoader } from '../../services/request';
import Notifications from '../../services/notifications';
import StripeCardProp from '../../commonComponents/propTypes/stripeCardProp';
import TenantProp, {
  PAYMENT_PROVIDER,
} from '../../commonComponents/propTypes/tenantProp';
import ShopModalOrderComplete from './shopItems/ShopModalOrderComplete';
import FormInput from '../../commonComponents/forms/FormInput';
import { useModal } from '../../services/customHooks';
import CurrencyProp from '../profile/propTypes/currencyProp';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import PaymentMethods from '../../commonComponents/modal/components/PaymentMethods';
import Loader from '../../services/loader';
import VatTypesProp from '../../commonComponents/forms/Address/VatTypesProp';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import {
  capturePaymentMarketplaceOrder,
  handleConfirmMarketplacePayment,
  updateMarketplaceOrder,
  getCreditCardList,
} from '../../services/checkoutHelper';
import PayOrderShippingTab from './components/pageToPayOrder/PayOrderShippingTab';
import organizationProp from '../../commonComponents/propTypes/organizationProp';
import MetaContext from '../../contextProviders/MetaContext';

function ShopUserOrderPay({
  t,
  order,
  creditCards,
  _csrf,
  tenant,
  redirect_status,
  payment_intent,
  currencies,
  vatTypes,
  countries,
  organization,
  typeOfPayment,
  paypalPaymentMethod,
  setupIntent,
}) {
  const {
    products,
    status,
    subtotal,
    discountTotal,
    shippingTotal,
    shippingTax,
    userDiscount,
    userDiscountTotal,
    currency,
    totalVA,
    shipping: shippingProvider,
    shippingPickupAddress,
  } = order;
  const noInvoice = !order.paymentId && !order?.invoice?.id;
  const { userPortalMenus } = useContext(MetaContext);
  const title =
    userPortalMenus.find(u => u.type === 'marketplace')?.name ||
    t('shop.market_place');
  const { settings = {} } = tenant;
  const { paymentMethods = [], paymentProviderType = PAYMENT_PROVIDER.NONE } =
    settings;
  const { loggedUser } = useContext(LoggedUserContext);
  const refShippingTab = useRef();

  const [hasPickupLocation, setPickupLocation] = useState(
    shippingPickupAddress,
  );
  // Check if the PROVIDER IS MONDIAL_RELAY
  const isMondialRelay =
    shippingProvider && shippingProvider.providerType === 'mondial_relay';

  const showVA = loggedUser && loggedUser?.type === 'collaborator';
  const symbol = findCurrencySymbol(currency, currencies);
  // Handle the two PAYMENT_METHOD
  const [tabActive, setTabActive] = useState(handleInitTab(paymentMethods));
  const [orderUpdated, setOrderUpdated] = useState(order);
  // Loading button Pay State
  const [isLoading, setIsLoading] = useState(false);
  const handleLoading = loading => {
    setIsLoading(loading);
  };
  const loader = useRef(new Loader('#shopUserOrderPay'));
  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);
  // Modal that shows after payment
  const [showModal, setShowModal] = useModal({ show: false, data: null });
  const [orderToUpdate, setOrderToUpdate] = useState(order);
  /** Handling the notes */
  const handleChange = field => value => {
    setOrderToUpdate(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };

  /** Handling Credit Card */
  const [userCreditCards, setUserCreditCards] = useState(creditCards);

  // Set the default credit card
  const defaultCardId = creditCards.find(c => c.isDefault === true);
  const [selectedCreditCard, setSelectedCreditCard] = useState(
    defaultCardId && defaultCardId.id ? defaultCardId.id : null,
  );

  useEffect(() => {
    const getCardsList = async () => {
      const cardsList = await getCreditCardList(_csrf, true);
      const defaultCard =
        cardsList && cardsList.length > 0
          ? cardsList.find(c => c.isDefault)?.id || null
          : null;
      setUserCreditCards(cardsList);
      setSelectedCreditCard(defaultCard);
    };

    getCardsList();
  }, []);

  // HANDLE THE ACCOUNT HOLDER NAME FOR BANCONTACT
  const [accountHolderName, setAccountHolderName] = useState(null);

  /** Handling go Back */
  const handleGoBack = () => {
    window.location = `/shop/order/${order._id}`;
  };

  /** Handling Payment */
  const loadingPurchase = loading => {
    if (loading) {
      showLoader();
    } else {
      hideLoader();
    }
  };

  const [intentCreated, setIntentCreated] = useState(null);

  /** BANCONTACT */
  const stripe = useStripe();
  const elements = useElements();

  const payOrderRequest = async updatedOrder => {
    if (redirect_status === 'success') {
      // If for some reason the user is able to click on the button pay, it should not execute the payment again
      return;
    }

    if (tabActive === 'bancontact') {
      if (!(accountHolderName && accountHolderName.length >= 3)) {
        Notifications.showNotificationError(
          t('error'),
          t('ERROR_BANCONTACT_INVALID'),
        );
        return;
      }
    }

    const paymentMethodId = selectedCreditCard || null;
    const isIntentOfPaymentCreated =
      (updatedOrder && updatedOrder.paymentId) || intentCreated; // intention of payment

    /** POST/PUT: CAPTURE PAYMENT / UPDATE PAYMENT INTENT MARKETPLACE ORDER */
    const {
      client_secret: paymentIntent_client_secret,
      status: paymentIntentStatus,
      payment_intent: paymentIntentResponse,
      runConfirm,
    } = await capturePaymentMarketplaceOrder(
      t,
      _csrf,
      updatedOrder,
      paymentMethodId,
      tabActive,
      isIntentOfPaymentCreated,
      null,
      paymentProviderType,
      true,
      stripe,
      elements,
      null,
      false,
      true,
    );

    setIntentCreated(paymentIntentResponse);

    if (runConfirm) {
      /** POST: CONFIRM PAYMENT MARKETPLACE ORDER */
      await handleConfirmMarketplacePayment(
        _csrf,
        updatedOrder._id,
        paymentIntentStatus,
        tabActive,
        paymentProviderType,
        paymentIntentResponse,
        paymentMethodId,
        paymentIntent_client_secret,
        stripe,
        elements,
        accountHolderName,
        () => {},
        order,
      );
    }
  };

  useEffect(() => {
    if (order.status === 'pending' || order.status === 'pending_payment') {
      loadingPurchase(true);

      // region normal confirmation without PayPal setup
      if (redirect_status === 'succeeded' && !typeOfPayment) {
        try {
          const confirmPayment = async () => {
            await handleConfirmMarketplacePayment(
              _csrf,
              order._id,
              redirect_status,
              order.paymentMethodType,
              paymentProviderType,
              payment_intent,
              null,
              null,
              stripe,
              elements,
              accountHolderName,
              () => {},
            );
          };

          confirmPayment();

          setShowModal({ show: true });
          loadingPurchase(true);
        } catch (err) {
          Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
        }
      }
      // endregion normal confirmation without PayPal setup

      // region confirmation without PayPal setup
      if (
        redirect_status === 'succeeded' &&
        typeOfPayment === PAYMENT_METHOD.PAYPAL &&
        paypalPaymentMethod
      ) {
        // only happens when we are setting up paypal
        try {
          const confirmPayment = async () => {
            if (setupIntent) {
              await capturePaymentMarketplaceOrder(
                t,
                _csrf,
                order,
                null,
                PAYMENT_METHOD.PAYPAL,
                order?.paymentId,
                order?.paymentId || null, // intention of payment
                paymentProviderType,
                false,
                stripe,
                null,
                paypalPaymentMethod,
                true,
              );
            }
          };

          confirmPayment()
            .then(() => {
              setShowModal({ show: true });
            })
            .catch(err => {
              Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
            });
          loadingPurchase(true);
        } catch (err) {
          Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
        }
      }
      // endregion confirmation without PayPal setup
      if (redirect_status === 'failed') {
        Notifications.showNotificationError(
          t('ERROR_PAYMENT_DENIED.TITLE'),
          t('ERROR_PAYMENT_DENIED.BANCONTACT'),
        );
      }
    }
  }, []);

  const payOrder = async () => {
    try {
      loadingPurchase(true);
      handleLoading(true);

      const notConsideringFee =
        noInvoice &&
        !order?.fee?.feeSubTotal > 0 &&
        orderUpdated?.fee?.feeSubTotal > 0;
      const shouldUpdateOrder =
        orderUpdated?.notes?.length > 0 || // if user added notes, update order object
        !_.isEqual(orderUpdated.companyBilling, orderToUpdate.companyBilling) ||
        (isMondialRelay && !hasPickupLocation) ||
        notConsideringFee;

      if (shouldUpdateOrder) {
        // BRAVON-6500: only PUT the fields this page actually edits. Sending
        // the whole cached order echoed a stale `status` back to the server,
        // rewinding a paid order to pending_payment.
        let params = {
          companyBilling: orderToUpdate.companyBilling,
        };
        if (isMondialRelay) {
          // if mondial relay and doesn't have the pickup id, focus and notification error
          if (!orderToUpdate?.shippingPickupAddress?.pickupId) {
            refShippingTab.current.focusMondialRelay();
            Notifications.showErrorFromRequest(
              'error',
              'error.select.pickup.mondial_relay',
            );
            loadingPurchase(false);
            handleLoading(false);
            return;
          }
          const { pickupId, ...pickupAddress } =
            orderToUpdate.shippingPickupAddress;
          params.shippingPickupId = pickupId;
          params.shippingPickupAddress = {
            ...pickupAddress,
            address: returnAddress(pickupAddress),
          };
          params.shippingPickupType = 'mondial_relay';
          setPickupLocation(params.shippingPickupAddress);
        }
        if (orderToUpdate?.notes?.length > 0) {
          params.notes = orderToUpdate.notes;
        }

        if (notConsideringFee) {
          params = {
            ...params,
            total: orderUpdated.total,
            subtotalTax: orderUpdated.subtotalTax,
            fee: orderUpdated.fee,
          };
        }
        const updatedOrder = await updateMarketplaceOrder(
          _csrf,
          orderToUpdate,
          params,
        );
        setOrderUpdated(updatedOrder);
        await payOrderRequest(updatedOrder);
      } else {
        await payOrderRequest(order);
      }
      loadingPurchase(false);
      handleLoading(false);
      setShowModal({ show: true });
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_PAYMENT_DENIED');
      loadingPurchase(false);
      handleLoading(false);
    }
  };

  const handlePaymentMethod = (type, payload) => {
    switch (type) {
      case 'changeTab':
        setTabActive(payload.tab);
        break;
      case 'updateHolderName':
        setAccountHolderName(payload.accountHolderName);
        break;
      case 'updatedCardSelected':
        setSelectedCreditCard(payload.cardId);
        break;
      case 'updatedUserCards':
        setUserCreditCards(payload.cards);
        setSelectedCreditCard(payload.cardId);
        break;
      case 'removeCreditCard':
        setUserCreditCards(payload.cards);
        break;
      case 'companyBilling':
        setOrderToUpdate(prevState => ({
          ...prevState,
          companyBilling: payload,
        }));
        break;
      default:
        console.log('*Default', type, payload);
    }
  };

  const calculatePaymentFee = async () => {
    try {
      // Axios Request to Create Order
      const { data: response } = await axios.post(
        `/api/market/order/${order._id}/fee/calculate`,
        { paymentMethodType: tabActive },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      const {
        fee,
        subtotalTax: subtotalTaxFromCalculate,
        total: totalFromCalculate,
      } = response.data || {};
      setOrderUpdated(prevOrder => ({
        ...prevOrder,
        fee,
        subtotalTax: subtotalTaxFromCalculate,
        total: totalFromCalculate,
      }));
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'ERROR_CALCULATE_FEE');
      console.error('ERROR', err);
    }
  };
  useEffect(() => {
    if (noInvoice) {
      calculatePaymentFee();
    }
  }, [tabActive]);

  /** RENDER Component */
  // Terms of conditions
  const [checkedTerms, setCheckedTerms] = useState(false);
  const handleCheckbox = () => {
    setCheckedTerms(!checkedTerms);
  };
  const { name } = tenant;
  const privacyPolicy = '/privacy-policy';
  const termsPolicy = '/terms-and-conditions';
  const appName = tenant && name ? name : 'Bravon';
  const termsMessage = (
    <div className="terms-box">
      {t('shop.product.terms.initial')}
      <a
        href={privacyPolicy}
        className="terms"
        target="_blank"
        rel="noreferrer"
      >{` ${t('shop.product.terms.privacy')} `}</a>
      {t('shop.product.terms.continuation')}
      <a href={termsPolicy} className="terms" target="_blank" rel="noreferrer">
        {` ${t('shop.product.terms.ofUse')} ${appName}`}
      </a>
      .
    </div>
  );
  // Prevent repetitive code
  const handleTotals = (
    description,
    value,
    isUserDiscount = false,
    hasPercentage,
  ) => {
    const totalValue = isUserDiscount
      ? `-${formatPrice(
          value,
          findCurrencySymbol(order.currency?.toUpperCase() || '€', currencies),
        )}`
      : formatPrice(
          value,
          findCurrencySymbol(order.currency?.toUpperCase() || '€', currencies),
        );
    return (
      <>
        <p className="col-lg-8">
          {!hasPercentage ? (
            t(`${description}`)
          ) : (
            <p>{`${t(`${description}`)} (${hasPercentage}%)`}</p>
          )}
        </p>
        <span className="col-lg-4">
          {typeof value === 'string' ? value : totalValue}
        </span>
      </>
    );
  };
  // Disable button Complete order
  const disablePurchaseButton = () => {
    switch (tabActive) {
      case PAYMENT_METHOD.CREDIT_CARD:
        return selectedCreditCard === null || !checkedTerms || isLoading;
      case PAYMENT_METHOD.BANCONTACT:
        return (
          !accountHolderName ||
          !checkedTerms ||
          accountHolderName.length === 0 ||
          isLoading
        );
      case PAYMENT_METHOD.BANK_TRANSFER:
      case PAYMENT_METHOD.CHEQUE:
      case PAYMENT_METHOD.CRYPTO_MANUAL:
      case PAYMENT_METHOD.PAYPAL:
      case PAYMENT_METHOD.KLARNA:
        return !checkedTerms || isLoading;
      case PAYMENT_METHOD.PAID_BY_COMPANY:
        return !orderToUpdate.companyBilling || !checkedTerms || isLoading;
      default:
        return true;
    }
  };

  const isPaidByCompany =
    order && order.paymentMethodType === PAYMENT_METHOD.PAID_BY_COMPANY;

  const renderPayOrderBtn =
    status === 'pending_payment' &&
    !isPaidByCompany &&
    redirect_status !== 'succeeded';

  // MONDIAL_RELAY HANDLING
  const handlePickupLocation = pickupAddress => {
    setOrderToUpdate(prevState => ({
      ...prevState,
      shippingPickupAddress: pickupAddress,
    }));
  };

  // PAGE
  return (
    <>
      {showModal.show && <ShopModalOrderComplete />}
      <Title title={title}>
        <ContentContainer>
          <SubHeader title={title} />
          <div className="m-content shop products-toPay-container">
            <div className="m-portlet__body">
              <div id="shopUserOrderPay" className="row col-lg-12">
                <div className="col-lg-9 col-md-12 col-12">
                  {/** PRODUCTS */}
                  <div className="m-portlet">
                    <div className="m-portlet__body m-portlet__body--no-padding">
                      <div className="m-invoice-2 products-toPay">
                        <h3 className="products-toPay-title">
                          {t('shop.order.pay.products')}
                        </h3>
                        <ul className="ul-products-toPay">
                          {products.map(item => {
                            const {
                              type,
                              packProduct,
                              product,
                              tax,
                              subtotal: subtotalProduct,
                              total: totalProduct,
                            } = item;
                            const isPack =
                              type === 'pack' || type === 'pack_advanced';
                            const productId = isPack
                              ? packProduct._id
                              : product._id;
                            const productName = isPack
                              ? packProduct.name
                              : product.name;
                            let image;
                            if (isPack) {
                              image =
                                packProduct?.images?.length > 0
                                  ? packProduct?.images[0].thumb
                                  : '/images/no_image_upload.png';
                            } else {
                              image =
                                product?.icon?.thumb?.length > 0
                                  ? product?.icon?.thumb
                                  : '/images/no_image_upload.png';
                            }
                            return (
                              <li
                                key={`product-${productId}`}
                                className="row list-product-toPay"
                              >
                                <div className="col-lg-2 col-md-12 col-12 product-toPay-img">
                                  <img src={image} alt={productName} />
                                </div>
                                <div className="col-lg-6 col-md-12 col-12 product-toPay-detail">
                                  <div className="product-toPay-detail-name">
                                    <h3>{productName}</h3>
                                    {order?.orderSubscription?.frequency ? (
                                      <span className="frequency">
                                        {t(
                                          `shop.product.subscription.frequency.${order?.orderSubscription?.frequency}`,
                                        )}
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="product-toPay-detail-quantity">
                                    <span className="product-toPay-label">
                                      {t('shop.order.pay.quantity')}
                                    </span>
                                    <span className="product-toPay-input">
                                      {item?.quantity}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-lg-4 col-md-12 col-12">
                                  <div className="row">
                                    <div className="col-5 price-pay mobile-border">
                                      <p>
                                        {t('shop.product.subtotal')}
                                        <br />
                                        {t('shop.product.subtotal.withoutTax')}
                                      </p>
                                      <h2>
                                        {formatPrice(subtotalProduct, symbol)}
                                      </h2>
                                    </div>
                                    <div className="col-6 price-pay mobile-border">
                                      <p>
                                        {t('shop.product.subtotal')}
                                        <br />
                                        {`${t(
                                          'shop.product.subtotal.withTax',
                                        )}, ${tax}%`}
                                      </p>
                                      <h2>
                                        {formatPrice(totalProduct, symbol)}
                                      </h2>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>
                  {/** SHIPPING */}
                  <PayOrderShippingTab
                    ref={refShippingTab}
                    order={order}
                    organization={organization}
                    pickupLocation={orderToUpdate.shippingPickupAddress || null}
                    onPickupLocation={handlePickupLocation}
                    countries={countries}
                    isMondialRelay={isMondialRelay}
                    showMondialRelay={!hasPickupLocation && !isLoading}
                    t={t}
                  />
                  {/** PAYMENT */}
                  <div className="m-portlet shop-order-blocks">
                    <div className="m-portlet__body m-portlet__body--no-padding">
                      <div className="m-invoice-2">
                        <div className="m-invoice-2 products-toPay payment-pay-page">
                          <PaymentMethods
                            t={t}
                            cards={userCreditCards}
                            updatePaymentMethod={handlePaymentMethod}
                            tabActive={tabActive}
                            accountHolderName={accountHolderName}
                            selectedCreditCard={selectedCreditCard}
                            handleIsLoading={handleLoading}
                            countries={countries}
                            vatTypes={vatTypes}
                            paidByCompanyInfo={
                              orderToUpdate.companyBilling || null
                            }
                            tenant={tenant}
                            paidByCompanyOpen={!isPaidByCompany}
                            isSubscription={order?.orderSubscription?.frequency}
                            order={order}
                            orderTotal={order.total}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/** SIDE ORDER */}
                <div className="col-lg-3 m-portlet order-sidebar order-toPay-sidebar">
                  <h6>{t('shop.order.pay.estimation')}</h6>
                  <hr />
                  <div className="order-toPay-sidebar-detail">
                    <div className="row">
                      {handleTotals(
                        'shop.order.pay.estimation.items',
                        subtotal,
                      )}
                    </div>
                    {order?.discountTotal > 0 ? (
                      <div className="row">
                        {handleTotals(
                          order?.isPaidWithCoins
                            ? 'shop.checkout.coupon.coins'
                            : 'shop.order.pay.estimation.discount',
                          discountTotal,
                        )}
                      </div>
                    ) : null}
                    {order && userDiscountTotal > 0 ? (
                      <div className="row">
                        {handleTotals(
                          'order_discount',
                          userDiscountTotal,
                          true,
                          userDiscount,
                        )}
                      </div>
                    ) : null}
                    {orderUpdated?.fee?.feeSubTotal ? (
                      <p className="feeContainer">
                        <span>
                          {`${t(
                            'order.fee',
                            `${orderUpdated?.fee?.feePercentage}%`,
                            formatPrice(
                              orderUpdated?.subtotal,
                              findCurrencySymbol(
                                order.currency?.toUpperCase() || '€',
                                currencies,
                              ),
                            ),
                          )}  (${t('checkout.payment.vatExcl')}):`}
                        </span>
                        <span className="items-price">
                          {formatPrice(
                            orderUpdated?.fee?.feeSubTotal,
                            findCurrencySymbol(
                              order.currency?.toUpperCase() || '€',
                              currencies,
                            ),
                          )}
                        </span>
                      </p>
                    ) : null}
                    <div className="row">
                      {handleTotals(
                        orderUpdated.subtotalTax === 0
                          ? 'vatFree'
                          : 'shop.order.detail.vat',
                        orderUpdated.subtotalTax === 0
                          ? ''
                          : orderUpdated.subtotalTax,
                      )}
                    </div>
                    {orderUpdated?.subtotalExtraTax > 0 && (
                      <div className="row">
                        {handleTotals(
                          'order.extraTax',
                          orderUpdated.subtotalExtraTax,
                        )}
                      </div>
                    )}
                    <div className="row">
                      {handleTotals(
                        'shop.order_shipping_cost',
                        shippingTotal,
                        false,
                        shippingTax,
                      )}
                    </div>
                  </div>
                  <hr />
                  {showVA && (
                    <div className="row order-pay-totalVA">
                      <p className="col-lg-8">{t('shop.product.totalVA')}</p>
                      <span className="col-lg-4">{totalVA || 0}</span>
                    </div>
                  )}
                  <div className="row order-toPay-sidebar-total">
                    {handleTotals(
                      'shop.order.pay.estimation.total',
                      orderUpdated.total,
                    )}
                  </div>
                  <hr />
                  <div className="order-toPay-sidebar-notes row">
                    <h5 className="col-lg-12 col-12">
                      {t('shop.order.pay.notes.header')}
                    </h5>
                    <FormInput
                      id="notes"
                      name="notes"
                      value={orderToUpdate?.notes}
                      onChange={handleChange('notes')}
                      placeholder={t('shop.order.pay.notes.placeholder')}
                      type="textarea"
                      disabled={status !== 'pending_payment' || order.invoice}
                      groupProps={{ className: 'notes' }}
                    />
                  </div>
                  <hr />
                  {renderPayOrderBtn && (
                    <div className="order-toPay-sidebar-terms">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for */}
                      <label className="m-checkbox m-checkbox--state-success">
                        <input
                          type="checkbox"
                          onChange={handleCheckbox}
                          checked={checkedTerms}
                        />
                        <span />
                      </label>
                      {termsMessage}
                    </div>
                  )}
                  <div className="row order-toPay-sidebar-buttons">
                    <div className="col-lg-6 col-6 remove-padding-order-toPay">
                      <button
                        type="button"
                        onClick={handleGoBack}
                        className="btn-cancel"
                      >
                        {t('shop.order.pay.cancel')}
                      </button>
                    </div>
                    <div className="col-lg-6 col-6 remove-padding-order-toPay">
                      {renderPayOrderBtn && (
                        <Anchor
                          onClick={payOrder}
                          className={`landing-certification-browse btn m-btn--pill btn-dynamic loader-button btn-complete${
                            disablePurchaseButton() ? ' disabled' : ''
                          }${
                            isLoading
                              ? ' m-loader m-loader--right m-loader--light'
                              : ''
                          }`}
                        >
                          {t('shop.order.pay')}
                        </Anchor>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ContentContainer>
      </Title>
    </>
  );
}

ShopUserOrderPay.propTypes = {
  t: PropTypes.func.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  creditCards: PropTypes.arrayOf(PropTypes.shape(StripeCardProp.propType))
    .isRequired,
  _csrf: PropTypes.string.isRequired,
  tenant: PropTypes.shape(TenantProp.propTypes).isRequired,
  redirect_status: PropTypes.string,
  payment_intent: PropTypes.string,
  setupIntent: PropTypes.string,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  organization: PropTypes.shape(organizationProp.propType).isRequired,
  typeOfPayment: PropTypes.string,
  paypalPaymentMethod: PropTypes.string,
};

ShopUserOrderPay.defaultProps = {
  redirect_status: '',
  payment_intent: '',
  typeOfPayment: null,
  paypalPaymentMethod: null,
  setupIntent: null,
};

export default translate(ShopUserOrderPay);
